<template>
  <div class="countdown-page" ref="animationContainer">
    <div class="header-row">
      <h1>Your Countdown</h1>
      <div class="header-actions">
        <button class="link-btn" @click="showSettings = true">Settings</button>
      </div>
    </div>

    <copy-link v-if="linkUrl" :url="linkUrl">
      <template #headline>Control Link</template>
      Share this link so others can control your countdown.
    </copy-link>

    <div v-if="loading">Loading countdown…</div>
    <div v-else-if="!sessionId || !state" class="no-session">
      <div class="error-message">
        <h2>❌ Could not load your countdown</h2>
        <p>I just don't know what went wrong…</p>
      </div>
    </div>
    <div v-else>
      <!-- Status row for active session -->
      <div class="status-row">
        <span class="label">Status:</span>
        <status-badge :status="state.status" />
      </div>

      <div class="timer">
        <div class="time" ref="timeDisplay" :class="[{ urgent: isUrgent }, failReasonClass]">
          {{ formatDuration(displayTimeLeft) }}
        </div>
        <div class="hint" v-if="state.status === 'created'">
          😴 Countdown not open yet. When you're edging and ready, open it to allow controllers to
          start your countdown at any random time of their choosing.
        </div>
        <div class="hint" v-else-if="state.status === 'ready'">
          👀 Countdown is open. Controllers may at any time start the countdown…
        </div>
        <div class="hint" v-else-if="state.status === 'failsafe'">
          ⏳ Failsafe period is active. Controllers or the system will start the countdown at any
          moment…
        </div>
        <div class="hint" v-else-if="state.status === 'countdown'">
          ⏱️ Countdown is running! You better cum!
        </div>
        <div class="hint paused" v-else-if="state.status === 'paused'">
          ⏸️ Countdown is paused. They are being mean to you…
        </div>
        <div class="hint fail-msg" v-else-if="state.status === 'fail'">
          {{ failMessage }}
        </div>
        <div class="hint success-msg" v-else-if="state.status === 'success'">
          ✅ You made it in time!
        </div>
        <div class="hint" v-else-if="state.status === 'aborted'">
          ⛔ You aborted your chance – you can still self-fail if you already came.
        </div>
        <div class="hint" v-else-if="state.status === 'closed'">
          🔒 Countdown closed. Create a new one when you're ready for the next edge.
        </div>
        <div class="hint" v-else>Waiting for controllers to allow you to cum…</div>
      </div>

      <div
        v-if="state.status === 'ready' || state.status === 'failsafe'"
        class="status-hint failsafe-info"
      >
        Failsafe mode is
        <span v-if="state.max_wait_left === null && state.random_wait_left === null">
          not configured, a controller has to start the countdown eventually.
        </span>
        <span v-else-if="state.max_wait_left === null">
          active, so the system can start the countdown at a random time within the next
          <computed-time :seconds="state.random_wait_left ?? 0" />
        </span>
        <span v-else-if="state.random_wait_left === null">
          configured to start the countdown in <computed-time :seconds="state.max_wait_left ?? 0" />
        </span>
        <span v-else>
          configured to start the countdown randomly between
          <computed-time :seconds="state.max_wait_left ?? 0" /> and
          <computed-time :seconds="state.random_wait_left ?? 0" />
        </span>
        <span v-if="state.max_wait_left !== null || state.random_wait_left !== null">
          automatically if a controller does not start it manually before then.
        </span>
      </div>

      <!-- Show punishment (if any) after a fail -->
      <div v-if="state.status === 'fail' && state.punishment_text" class="punishment-box">
        <div class="punishment-title">Punishment</div>
        <div class="punishment-text">{{ state.punishment_text }}</div>
      </div>

      <div class="actions primary-actions">
        <button v-if="can('open')" @click="openSession" class="open-btn">Open Countdown</button>
        <button v-if="can('trigger')" @click="markSuccess" class="success-btn">I came</button>
      </div>

      <section class="actions secondary-actions">
        <button
          v-if="can('close')"
          @click="closeSession"
          :disabled="closing"
          class="link-btn close"
        >
          Close Countdown
        </button>
        <button
          v-if="can('abort')"
          @click="abortSession"
          :disabled="aborting"
          class="link-btn close"
        >
          Abort Countdown session
        </button>
        <button v-if="can('reset')" class="link-btn" @click="resetLink">Reset control link</button>
        <router-link to="/countdown/setup" v-if="state.status === 'closed'" class="link-btn">
          New countdown session
        </router-link>
      </section>

      <section class="controllers">
        <h2>Connected controllers</h2>
        <div v-if="controllers.length" class="avatars">
          <div class="avatar" v-for="c in controllers" :key="JSON.stringify(c)">
            <mini-user
              :online="c?.hide_online_status ? null : (c?.online ?? undefined)"
              :fullname="c.name"
              :photo_url="c.photo_url"
              :username="c.username"
              :user_id="c.id"
              :photo-fallback="ponyAvatar()"
            />
          </div>
        </div>
        <p v-else-if="session?.connected" class="muted">
          Nobody is connected right now. Share your control link so others can join.
        </p>
        <p v-else class="muted">You are not connected right now.</p>
      </section>

      <!-- Settings overlay -->
      <div v-if="showSettings" class="overlay" @click.self="showSettings = false">
        <div class="panel">
          <div class="panel-header">
            <h2>Settings</h2>
            <button class="link-btn" @click="showSettings = false">Close</button>
          </div>
          <div class="privacy-panel">
            <h3>Privacy & Visibility</h3>

            <div class="field">
              <label for="visibility">Visibility</label>
              <select id="visibility" v-model="form.visibility">
                <option value="link">Link only</option>
                <option value="friends">Friends (default)</option>
                <option value="public">Public</option>
              </select>
            </div>

            <div class="grid">
              <div class="field">
                <label>Your anonymity</label>
                <anon-settings v-model="ownerAnon" whose="my" />
              </div>
              <div class="field">
                <label>Default for controllers</label>
                <anon-settings v-model="controllerDefaultAnon" whose="their" :user="AnonUser" />
                <p class="note">Controllers can override these defaults for themselves.</p>
              </div>
            </div>

            <h3>Countdown</h3>

            <div class="field">
              <label for="length-seconds">
                Countdown length
                <computed-time :seconds="lengthField" />
              </label>
              <div class="length-input">
                <input
                  id="length-seconds"
                  type="text"
                  :disabled="lengthSaving || !can('set_length')"
                  v-model="lengthField"
                  placeholder="seconds"
                />
                <button class="link-btn" :disabled="lengthSaving" @click="applyLengthChange">
                  {{ lengthSaving ? 'Saving…' : 'Apply' }}
                </button>
              </div>
              <div class="help">This changes your current countdown.</div>
              <div class="help">Current: {{ formatDuration(state.countdown_seconds) }}.</div>
            </div>

            <div class="grid">
              <div class="field">
                <label for="max-wait">
                  Max wait before auto-start (sec)
                  <computed-time :seconds="form.max_wait_seconds" />
                </label>
                <div class="length-input optional-input">
                  <input
                    id="random-wait-null"
                    type="checkbox"
                    :checked="form.max_wait_seconds !== null"
                    @click="form.max_wait_seconds = form.max_wait_seconds === null ? 7200 : null"
                  />
                  <input
                    id="max-wait"
                    type="number"
                    min="0"
                    max="86400"
                    step="5"
                    v-model.number="form.max_wait_seconds"
                    placeholder="optional"
                  />
                </div>
              </div>
              <div class="field">
                <label for="random-wait">
                  Auto-start window (sec)
                  <computed-time :seconds="form.random_wait_seconds" />
                </label>
                <div class="length-input optional-input">
                  <input
                    id="random-wait-null"
                    type="checkbox"
                    :checked="form.random_wait_seconds !== null"
                    @change="
                      form.random_wait_seconds = form.random_wait_seconds === null ? 600 : null
                    "
                  />
                  <input
                    id="random-wait"
                    type="number"
                    min="0"
                    max="86400"
                    step="5"
                    v-model.number="form.random_wait_seconds"
                    placeholder="optional"
                  />
                </div>
              </div>
            </div>

            <div class="actions">
              <button class="link-btn" :disabled="saving" @click="savePrivacy">
                {{ saving ? 'Saving…' : 'Save' }}
              </button>
              <span v-if="saved" class="saved">Saved!</span>
            </div>
          </div>
        </div>
      </div>

      <p v-if="error" class="error">{{ error }}</p>
    </div>

    <debug-widget :data="state" name="state" :open="false" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { apiFetch } from '@/api'
import { useAuthStore } from '@/stores/auth'
import { useWsStore, type SessionID, type SessionStateOut } from '@/stores/ws'
import { useToastStore } from '@/stores/toast'
import { ponyAvatar } from '@/utils/images'
import { useAnimations } from '@/composables/useAnimations'
import { useSessionActivity } from '@/composables/useSessionActivity'
import { useNotifications } from '@/composables/useNotifications'
import { formatDuration } from '@/utils/date'
import StatusBadge from '@/components/StatusBadge.vue'
import type { ControllerOut } from '@/schemas/schemas.ts'
import CopyLink from '@/components/CopyLink.vue'
import AnonSettings from '@/components/AnonSettings.vue'
import { AnonUser } from '@/components/AnonSettings.vue'
import DebugWidget from '@/components/DebugWidget.vue'
import MiniUser from '@/components/MiniUser.vue'
import ComputedTime from '@/components/ComputedTime.vue'

const router = useRouter()
const auth = useAuthStore()
const ws = useWsStore()
const toasts = useToastStore()
const { requestPermission, notifyEvent } = useNotifications()

const sessionId = ref<SessionID | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
const controllers = ref<ControllerOut[]>([])
const closing = ref(false)
const aborting = ref(false)
const showSettings = ref(false)
const linkUrl = ref<string | null>(null)

const can = computed(() => ws.useCan(sessionId.value, 'owner'))

const session = computed(() => (sessionId.value ? ws.getSession(sessionId.value) : null))
const state = computed(() =>
  session.value
    ? session.value.state
    : {
        status: 'created' as const,
        countdown_seconds: 0,
        controllers: [],
        time_left: null,
        fail_reason: null,
        allowed_cmds: { owner: [], controller: [] },
      },
)
const displayTimeLeft = computed(() => {
  // Always prefer time_left when available
  if (state.value?.status === 'fail') {
    return 0
  }
  if (state.value?.time_left !== null && state.value?.time_left !== undefined) {
    return state.value.time_left
  }
  // Only fall back to countdown_seconds for created/ready state or when no time_left is available
  if (state.value?.status === 'ready' || state.value?.status === 'created') {
    return state.value.countdown_seconds
  }
  return 0
})

// Permissions from backend

const {
  animationContainer,
  createSuccessAnimation,
  createFailureAnimation,
  addCountdownTickEffect,
  addUrgentPulse,
} = useAnimations()

const timeDisplay = ref<HTMLElement | null>(null)
let removeUrgentPulse: (() => void) | null = null

// Use window focus handling for automatic watch/unwatch
useSessionActivity(() => (sessionId.value ? sessionId.value : null))

// Live length editing for the existing session (immediate apply with debouncing)
const lengthField = ref<number | null>(null)
const lengthSaving = ref(false)

watch(
  () => state.value?.countdown_seconds,
  (sec) => {
    if (sec === undefined || sec === null) {
      return
    }
    if (!lengthSaving.value) {
      lengthField.value = sec
    }
  },
  { immediate: true },
)

async function applyLengthChange() {
  if (!sessionId.value) {
    return
  }
  if (!can.value('set_length')) return

  if (lengthField.value === state.value?.countdown_seconds) {
    toasts.add(`Countdown length is already set to ${lengthField.value} seconds.`, 'info')
    return
  }
  lengthSaving.value = true
  try {
    ws.sendCmd(sessionId.value, 'set_length', { seconds: lengthField.value })
    toasts.add(`Countdown length set to ${lengthField.value} seconds.`, 'success')
  } finally {
    lengthSaving.value = false
  }
}
async function openSession() {
  if (!sessionId.value) return
  if (!can.value('open')) return
  ws.sendCmd(sessionId.value, 'open')
  toasts.add('Countdown opened', 'info')
}

async function closeSession() {
  if (!sessionId.value) return
  if (!can.value('close')) return
  closing.value = true
  try {
    ws.sendCmd(sessionId.value, 'close')
    toasts.add('Countdown closed', 'info')
  } finally {
    closing.value = false
  }
}

async function abortSession() {
  if (!sessionId.value) return
  if (!can.value('abort')) return
  aborting.value = true
  try {
    ws.sendCmd(sessionId.value, 'abort')
    toasts.add('Countdown aborted', 'info')
  } finally {
    aborting.value = false
  }
}

async function resetLink() {
  if (!auth.user) {
    toasts.add('Please log in to reset your control link', 'info')
    router.push('/login')
    return
  }
  const resp = await apiFetch('/api/me/control-link/reset', { method: 'POST' })
  if (resp.ok) {
    const data = (await resp.json()) as { url: string }
    linkUrl.value = data.url
    toasts.add('Control link reset successfully', 'success')
  }
}

const isUrgent = computed(
  () =>
    state.value?.status === 'countdown' &&
    (state.value.time_left ?? state.value.countdown_seconds) <= 10,
)
const isVeryUrgent = computed(
  () =>
    state.value?.status === 'countdown' &&
    (state.value.time_left ?? state.value.countdown_seconds) <= 5,
)

onMounted(async () => {
  if (!auth.user && auth.token) await auth.loadMe()
  if (!auth.user) {
    toasts.add('Please log in to view your countdown', 'info')
    router.push('/login')
    return
  }
  requestPermission()
  try {
    const resp = await apiFetch('/api/me/active-session')
    if (resp.ok) {
      const session = (await resp.json()) as { id: SessionID } | null
      if (session) {
        sessionId.value = session.id
        ws.startSession(session.id, 'owner')
        ws.startActivityTracking(session.id)
        await loadPrivacy()
        await loadLink()
        loading.value = false
        await nextTick()
        return
      }
    }
    // No active session: go to setup page
    router.push('/countdown/setup')
  } catch (err) {
    error.value = 'Failed to load your countdown session'
    toasts.add(`Failed to load your countdown session (${err})`, 'error')
    router.push('/login')
  }
})

// Cleanup when leaving the page
onBeforeUnmount(() => {
  if (removeUrgentPulse) {
    removeUrgentPulse()
    removeUrgentPulse = null
  }
  document.body.classList.remove('very-urgent')
  if (sessionId.value) {
    ws.stopActivityTracking(sessionId.value)
  }
})

// Owner-side action button: rely on allowed_cmds

const isFail = computed(() => state.value?.status === 'fail')
const isFailTimeout = computed(() => isFail.value && state.value?.fail_reason === 'timeout')
const isFailInvalid = computed(() => isFail.value && state.value?.fail_reason === 'invalid')
const failReasonClass = computed(() => {
  if (isFailTimeout.value) return 'fail-timeout'
  if (isFailInvalid.value) return 'fail-invalid'
  return ''
})
const failMessage = computed(() => {
  if (!isFail.value) return ''
  if (isFailTimeout.value) return "⏰ Time's up! You missed your window!"
  return '🚫 You were not allowed to cum.'
})

async function markSuccess() {
  if (!sessionId.value) return
  if (!can.value('trigger')) return
  ws.sendCmd(sessionId.value, 'trigger')
}

// Toast on controller join/leave
let prevControllers = new Map<number, string>()
watch(
  () => state.value?.controllers ?? [],
  (curr) => {
    const currentMap = new Map<number, string>(
      (curr as ControllerOut[]).map((c) => [c.id, c.name || 'Anonymous']),
    )
    for (const [id, name] of currentMap) {
      if (!prevControllers.has(id)) {
        notifyEvent(`🎯 ${name} is now watching you!`, 'success', { verboseOnly: true })
        setTimeout(() => {
          if (Math.random() > 0.7) {
            const heart = document.createElement('div')
            heart.className = 'floating-heart'
            heart.textContent = '👀'
            heart.style.left = `${Math.random() * window.innerWidth}px`
            heart.style.top = `${window.innerHeight - 50}px`
            document.body.appendChild(heart)
            setTimeout(() => {
              if (document.body.contains(heart)) document.body.removeChild(heart)
            }, 3000)
          }
        }, 500)
      }
    }
    for (const [id, name] of prevControllers) {
      if (!currentMap.has(id)) {
        notifyEvent(`😢 ${name} stopped watching`, 'warning', { verboseOnly: true })
      }
    }
    prevControllers = currentMap
    controllers.value = curr as ControllerOut[]
  },
)

// Status-driven effects
watch(
  () =>
    [state.value?.status, state.value?.fail_reason] as [
      SessionStateOut['status'] | undefined,
      SessionStateOut['fail_reason'] | undefined,
    ],
  ([newStatus, newReason], [oldStatus, oldReason]) => {
    if (newStatus === undefined) {
      return
    }
    if (newStatus === 'success' && oldStatus !== 'success') {
      if (removeUrgentPulse) {
        removeUrgentPulse()
        removeUrgentPulse = null
      }
      createSuccessAnimation()
      notifyEvent('🎉 Congratulations!', 'success', {
        body: 'You made it in time!',
        requireInteraction: true,
        autoClose: 0,
      })
    } else if (newStatus === 'fail' && (oldStatus !== 'fail' || newReason !== oldReason)) {
      createFailureAnimation(newReason)
      if (removeUrgentPulse) {
        removeUrgentPulse()
        removeUrgentPulse = null
      }
      if (newReason === 'timeout') {
        notifyEvent("❌ Time's up!", 'error', {
          body: 'You were not allowed to cum!',
          requireInteraction: true,
          autoClose: 0,
        })
      } else if (newReason === 'invalid') {
        notifyEvent('🚫 Not allowed!', 'error', {
          body: 'You came when not allowed!',
          requireInteraction: true,
          autoClose: 0,
        })
      } else {
        notifyEvent('🚫 You failed!', 'error', { requireInteraction: true, autoClose: 0 })
      }
    } else if (newStatus === 'countdown' && oldStatus === 'paused') {
      notifyEvent('▶️ Countdown resumed!', 'info')
    } else if (newStatus === 'countdown' && oldStatus !== 'countdown') {
      notifyEvent('⏰ Countdown started!', 'info', { body: 'Good luck!' })
    } else if (newStatus === 'paused' && oldStatus === 'countdown') {
      notifyEvent('⏸️ Paused', 'warning', { body: 'Countdown paused by controller' })
      if (removeUrgentPulse) {
        removeUrgentPulse()
        removeUrgentPulse = null
      }
    } else if (newStatus === 'aborted' && oldStatus !== 'aborted') {
      notifyEvent('🛑 Aborted', 'warning', { body: 'Countdown was aborted' })
      if (removeUrgentPulse) {
        removeUrgentPulse()
        removeUrgentPulse = null
      }
    } else if (newStatus === 'created' && oldStatus && oldStatus !== 'created') {
      notifyEvent('🆕 Session reset to created', 'info', { verboseOnly: true })
    } else if (newStatus === 'closed' && oldStatus !== 'closed') {
      notifyEvent('🔒 Session closed', 'info', { verboseOnly: true })
    }
  },
)

// Tick effect
watch(
  () => state.value?.time_left,
  (newTime, oldTime) => {
    if (
      newTime !== undefined &&
      oldTime !== undefined &&
      newTime !== null &&
      oldTime !== null &&
      newTime < oldTime &&
      timeDisplay.value
    ) {
      addCountdownTickEffect(timeDisplay.value)

      if (newTime === 30) {
        notifyEvent('⚡ 30 seconds left!', 'warning', { autoClose: 4000 })
      } else if (newTime === 10) {
        notifyEvent('🚨 FINAL 10 SECONDS!', 'error', { autoClose: 4000 })
      } else if (newTime === 5) {
        notifyEvent('💀 5… 4… 3…', 'error', { autoClose: 4000 })
      } else if (newTime > 60 && newTime % 60 === 0) {
        const mins = newTime / 60
        notifyEvent(`⏱️ ${mins} minute${mins !== 1 ? 's' : ''} left`, 'info', {
          verboseOnly: true,
          noToast: true,
          autoClose: 4000,
        })
      } else if (newTime <= 60 && newTime > 10 && newTime !== 30 && newTime % 10 === 0) {
        notifyEvent(`⏱️ ${newTime} seconds left`, 'info', {
          verboseOnly: true,
          noToast: true,
          autoClose: 4000,
        })
      }
    }
  },
)

// Urgency effects
watch(
  () => isUrgent.value,
  (urgent) => {
    if (urgent && timeDisplay.value && !removeUrgentPulse) {
      removeUrgentPulse = addUrgentPulse(timeDisplay.value)
    } else if (!urgent && removeUrgentPulse) {
      removeUrgentPulse()
      removeUrgentPulse = null
    }
  },
)

watch(
  () => isVeryUrgent.value,
  (veryUrgent) => {
    if (veryUrgent && document.body) {
      document.body.classList.add('very-urgent')
    } else {
      document.body.classList.remove('very-urgent')
    }
  },
)

const saving = ref(false)
const saved = ref(false)

type Visibility = 'link' | 'friends' | 'public'

const form = ref({
  visibility: 'friends' as Visibility,
  owner_anon_username: false,
  owner_anon_name: false,
  owner_anon_photo: false,
  controller_default_anon_username: false,
  controller_default_anon_name: false,
  controller_default_anon_photo: false,
  max_wait_seconds: null as number | null,
  random_wait_seconds: null as number | null,
})

// Bridge models for <anon-settings>
const ownerAnon = computed({
  get: () => ({
    anon_username: form.value.owner_anon_username,
    anon_name: form.value.owner_anon_name,
    anon_photo: form.value.owner_anon_photo,
  }),
  set: (v: { anon_username: boolean; anon_name: boolean; anon_photo: boolean }) => {
    form.value.owner_anon_username = v.anon_username
    form.value.owner_anon_name = v.anon_name
    form.value.owner_anon_photo = v.anon_photo
  },
})

const controllerDefaultAnon = computed({
  get: () => ({
    anon_username: form.value.controller_default_anon_username,
    anon_name: form.value.controller_default_anon_name,
    anon_photo: form.value.controller_default_anon_photo,
  }),
  set: (v: { anon_username: boolean; anon_name: boolean; anon_photo: boolean }) => {
    form.value.controller_default_anon_username = v.anon_username
    form.value.controller_default_anon_name = v.anon_name
    form.value.controller_default_anon_photo = v.anon_photo
  },
})

function clampSeconds(n: number): number {
  return Math.max(0, Math.min(86400, Math.floor(n)))
}
function normalizeOptionalSeconds(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v as number) || (v as number) < 0) return null
  return clampSeconds(v as number)
}
async function loadPrivacy() {
  if (!sessionId.value) return
  const resp = await apiFetch(`/api/sessions/${sessionId.value}`)
  if (resp.ok) {
    const raw = (await resp.json().catch(() => ({}))) as unknown
    let visibility: 'link' | 'friends' | 'public' = 'friends'
    if (typeof raw === 'object' && raw && 'visibility' in (raw as Record<string, unknown>)) {
      const vv = (raw as Record<string, unknown>)['visibility']
      if (typeof vv === 'string' && (vv === 'link' || vv === 'friends' || vv === 'public')) {
        visibility = vv
      }
    }
    const getFlag = (k: string): boolean => {
      if (typeof raw === 'object' && raw && k in (raw as Record<string, unknown>)) {
        return (raw as Record<string, unknown>)[k] === true
      }
      return false
    }
    const getNumOrNull = (k: string): number | null => {
      if (typeof raw === 'object' && raw && k in (raw as Record<string, unknown>)) {
        const v = (raw as Record<string, unknown>)[k]
        if (v === null) return null
        if (typeof v === 'number') return normalizeOptionalSeconds(v)
      }
      return null
    }

    form.value.visibility = visibility
    form.value.owner_anon_username = getFlag('owner_anon_username')
    form.value.owner_anon_name = getFlag('owner_anon_name')
    form.value.owner_anon_photo = getFlag('owner_anon_photo')
    form.value.controller_default_anon_username = getFlag('controller_default_anon_username')
    form.value.controller_default_anon_name = getFlag('controller_default_anon_name')
    form.value.controller_default_anon_photo = getFlag('controller_default_anon_photo')
    form.value.max_wait_seconds = getNumOrNull('max_wait_seconds')
    form.value.random_wait_seconds = getNumOrNull('random_wait_seconds')
  }
}

async function loadLink() {
  if (!auth.user) return
  const resp = await apiFetch('/api/me/control-link', { method: 'POST' })
  if (resp.ok) {
    const data = (await resp.json()) as { url: string }
    linkUrl.value = data.url
  }
}

async function savePrivacy() {
  if (!sessionId.value) return
  saving.value = true
  try {
    const payload = {
      visibility: form.value.visibility,
      owner_anon_username: form.value.owner_anon_username,
      owner_anon_name: form.value.owner_anon_name,
      owner_anon_photo: form.value.owner_anon_photo,
      controller_default_anon_username: form.value.controller_default_anon_username,
      controller_default_anon_name: form.value.controller_default_anon_name,
      controller_default_anon_photo: form.value.controller_default_anon_photo,
      max_wait_seconds: normalizeOptionalSeconds(form.value.max_wait_seconds),
      random_wait_seconds: normalizeOptionalSeconds(form.value.random_wait_seconds),
    }
    const resp = await apiFetch(`/api/sessions/${sessionId.value}/privacy`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    if (resp.ok) {
      saved.value = true
      setTimeout(() => (saved.value = false), 1500)
      toasts.add('Privacy updated', 'success')
      await loadPrivacy()
    } else {
      toasts.add('Failed to save privacy settings', 'error')
    }
  } catch (err) {
    toasts.add(`Error saving privacy settings: ${err}`, 'error')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped lang="scss">
.countdown-page {
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--color-background);
  color: var(--color-text);
  border-radius: 1rem;
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .header-actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .status-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .label {
    opacity: 0.7;
  }

  .timer {
    text-align: center;
    margin: 1rem 0;
  }

  .time {
    font-size: 3.25rem;
    font-weight: 800;
    letter-spacing: 0.03em;
    font-variant-numeric: tabular-nums;
  }

  .time.urgent {
    animation: pulse 1s infinite;
    color: #e74c3c;
  }

  .time.fail-timeout {
    color: #c0392b;
  }

  .time.fail-invalid {
    color: #8e44ad;
    animation: shake 0.4s ease;
  }

  .hint {
    opacity: 0.7;
    margin-top: 0.25rem;
  }

  .hint.paused {
    color: #e67e22;
  }

  .hint.fail-msg {
    font-weight: 600;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    margin: 1rem 0;
    flex-wrap: wrap;
  }

  button {
    background: transparent;
    color: var(--color-text);
    border: 1px solid var(--color-border);
    padding: 0.6rem 1rem;
    border-radius: 0.6rem;
    cursor: pointer;
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .success-btn {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: #fff;
    border: none;
    padding: 0.8rem 1.25rem;
    font-weight: 700;
    font-size: 1.05rem;
    box-shadow: 0 6px 18px rgba(34, 197, 94, 0.35);
    transition:
      transform 0.08s ease,
      box-shadow 0.15s ease;
  }

  .success-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(34, 197, 94, 0.45);
  }

  .success-btn:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.35);
  }

  .link-btn {
    &.create {
      border-style: dashed;
    }

    &.copy {
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: #fff;
      border: none;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }

    &.copy:hover {
      box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
    }

    &.copy:active {
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .controllers {
    margin-top: 1.5rem;
  }

  .avatars {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 0.75rem;
  }

  .avatar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 1px solid var(--color-border);
    border-radius: 0.5rem;
    background: var(--color-background-soft);
  }

  .avatar img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
  }

  .muted {
    opacity: 0.7;
  }

  .error {
    color: #c0392b;
    margin-top: 1rem;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-4px);
    }
    50% {
      transform: translateX(4px);
    }
    75% {
      transform: translateX(-2px);
    }
  }

  // Floating heart animation
  .floating-heart {
    position: fixed;
    width: 50px;
    height: 50px;
    pointer-events: none;
    font-size: 1.5rem;
    animation: float 3s ease-in-out forwards;

    &.heart {
      color: #e74c3c;
      transform: scale(1.2);
    }
  }

  @keyframes float {
    0% {
      transform: translateY(0) scale(1);
    }
    50% {
      transform: translateY(-30px) scale(1.1);
    }
    100% {
      transform: translateY(-60px) scale(0.9);
    }
  }

  // Very urgent state effects
  &.very-urgent {
    .time {
      color: #c0392b;
      animation: none;
    }

    .hint {
      color: #c0392b;
      font-weight: bold;
    }

    .badge {
      &.countdown {
        background: rgba(192, 57, 43, 0.15);
        border-color: rgba(192, 57, 43, 0.35);
      }

      &.paused {
        background: rgba(192, 57, 43, 0.15);
        border-color: rgba(192, 57, 43, 0.35);
      }
    }
  }

  .punishment-box {
    margin: 0.75rem auto 0;
    max-width: 720px;
    border: 1px solid var(--color-border);
    background: var(--color-background-soft);
    border-radius: 0.6rem;
    padding: 0.75rem 0.9rem;

    .punishment-title {
      font-weight: 700;
      margin-bottom: 0.25rem;
      opacity: 0.8;
    }

    .punishment-text {
      white-space: pre-wrap;
      line-height: 1.35;
    }
  }
}

/* Settings overlay styles */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
}

.panel {
  width: min(900px, 96vw);
  max-height: 92vh;
  overflow: auto;
  background: var(--color-background);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 0.8rem;
  padding: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
h3 {
  margin-top: 1rem;
}

.privacy-panel {
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1rem;
    margin-top: 0.75rem;
  }

  .field {
    border: 1px solid var(--color-border);
    border-radius: 0.5rem;
    padding: 0.75rem;
    background: var(--color-background-soft);
  }

  label {
    font-weight: 600;
    display: block;
    margin-bottom: 0.4rem;
  }

  .length-input {
    display: grid;
    align-items: center;
    gap: 0.5rem;
    grid-template-columns: 1fr;
    &.optional-input {
      grid-template-columns: 1em 1fr;
    }
  }

  select {
    width: 100%;
    padding: 0.5rem;
    border-radius: 0.5rem;
    background: var(--color-background);
    color: var(--color-text);
    border: 1px solid var(--color-border);
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .saved {
    color: #22c55e;
  }

  .note {
    margin-top: 0.35rem;
    font-size: 0.85rem;
    color: var(--color-text-muted);
  }
}

/* Minor style for seconds suffix */
.suffix {
  align-self: center;
  opacity: 0.8;
  padding-left: 0.4rem;
}
</style>
